import type { DataTableSortState } from '@/components/DataTable/DataTable';
import type { TaskFilters } from '@/store/tasks/task-filters';
import type {
  TasksFilter,
  TasksSortField,
  TaskStatusType,
  TaskTypes,
} from '@/store/tasks/tasks.dto';
import type { TasksListFilter } from '@/store/tasks/tasks.queries';

import { EVERYONE } from './AssigneeCut';

/**
 * Rows per request before the desktop list has measured how many fit. That list holds
 * its request until the measurement lands, so this is the shape of the filter while
 * nothing is being asked for rather than a size the list is ever fetched at.
 */
export const DESKTOP_PAGE_SIZE = 12;
export const MOBILE_PAGE_SIZE = 50;

/** The desktop table's sortable columns — every one activity can order by. */
export const TASKS_SORT_FIELDS = [
  'title',
  'responsible',
  'due',
  'priority',
] as const satisfies TasksSortField[];

/**
 * The page's filter as activity's, in one place.
 *
 * Every field here is filtered by the service — status, priority, responsible, type,
 * dates and `equipment_id=none` all have counterparts on `GET /tasks` — so the list the
 * page renders is the list it asked for, with no client-side second pass. Blank values
 * become `undefined` rather than empty strings so that two filters that mean the same
 * thing hash to the same query key.
 */
export const toTasksFilter = (
  filters: TaskFilters,
  limit: number,
  sort?: DataTableSortState | null,
): TasksListFilter => ({
  limit,
  starts_from: filters.startDate || undefined,
  ends_to: filters.endDate || undefined,
  type: (filters.type || undefined) as TaskTypes | undefined,
  status: filters.status.length ? (filters.status as TaskStatusType[]) : undefined,
  priority: (filters.priority || undefined) as TasksFilter['priority'],
  responsible: filters.responsible || undefined,
  // The Tasks page is the non-equipment list; equipment jobs belong to Maintenance.
  // Activity reads 'none' as "equipment is null", so this cut is the service's, not ours.
  equipment_id: 'none',
  // Spread rather than set to `undefined`, so an unsorted list keeps the key it had.
  ...(sort && { sort: sort.field as TasksSortField, order: sort.direction }),
});

/** The filter the page lands on: my still-open tasks. */
export const defaultTaskFilters = (currentUserId?: string): TaskFilters => ({
  startDate: '',
  endDate: '',
  type: '',
  status: ['pending'],
  priority: '',
  responsible: currentUserId ?? EVERYONE,
  equipment: '',
});

/**
 * Whether the list is showing its landing cut rather than one the user chose — which is
 * what decides between "no tasks yet" and "no tasks match".
 */
export const isDefaultTaskFilters = (filters: TaskFilters, currentUserId?: string): boolean =>
  !filters.startDate &&
  !filters.endDate &&
  !filters.type &&
  !filters.equipment &&
  !filters.priority &&
  filters.responsible === (currentUserId ?? EVERYONE) &&
  filters.status.length === 1 &&
  filters.status[0] === 'pending';
